// Churchill DSCR Calculator Component
// Full-featured DSCR loan calculator with LLPA pricing engine

import React, { useState, useMemo, useEffect } from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import {
  Building2,
  DollarSign,
  Calculator as CalcIcon,
  Download,
  Landmark,
  Wallet,
  Settings,
  FileStack,
  Receipt,
  ChevronDown,
  ChevronUp,
  Upload,
  RefreshCw,
  Banknote,
  MapPin,
  TrendingUp,
  Target,
  FileText,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Separator } from './ui/separator';
import { formatCurrency, formatPercentage } from '../utils/formatters';
import {
  calculateChurchillMetrics,
  getRateMatrix,
  getProductTypes,
  PROPERTY_TYPES,
  LOAN_PURPOSES,
  AMORTIZATION_TYPES,
} from '../utils/churchillCalculations';
import {
  PROGRAM_OPTIONS,
  FICO_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
  LOAN_PURPOSE_OPTIONS,
  DSCR_OPTIONS,
  PREPAY_PENALTY_OPTIONS,
  LOCK_PERIOD_OPTIONS,
  US_STATES,
  CHURCHILL_PROGRAMS,
} from '../data/churchillRateData';
import { ChurchillDSCRPDF } from './PDFDocument';
import { isAdmin as checkIsAdmin } from '../config/loanOfficers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Currency Input Component
const CurrencyInput = ({ value, onChange, placeholder, id, testId }) => {
  const handleChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9.]/g, '');
    onChange(rawValue);
  };

  const displayValue = value ? parseFloat(value).toLocaleString('en-US', { maximumFractionDigits: 0 }) : '';

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#637888] font-mono">$</span>
      <Input
        id={id}
        data-testid={testId}
        type="text"
        value={displayValue}
        onChange={handleChange}
        placeholder={placeholder}
        className="pl-7 font-mono-number mt-1"
      />
    </div>
  );
};

// Percentage Input Component
const PercentageInput = ({ value, onChange, placeholder, id, testId }) => {
  return (
    <div className="relative">
      <Input
        id={id}
        data-testid={testId}
        type="number"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pr-7 font-mono-number mt-1"
      />
      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#637888] font-mono">%</span>
    </div>
  );
};

// Initial State
const initialInputs = {
  // Property Info
  projectName: '',
  propertyAddress: '',
  propertyState: 'FL',
  
  // Program Selection
  programName: 'DSCR Base',
  productType: '30Y Fixed',
  
  // Loan Details
  propertyValue: '',
  purchasePrice: '',
  loanAmount: '',
  
  // Borrower Info
  ficoScore: '740',
  
  // Property & Terms
  propertyType: 'Single Family',
  loanPurpose: 'Purchase',
  
  // DSCR Information
  dscrRatio: '1.25',
  monthlyRent: '',
  monthlyTaxes: '',
  monthlyInsurance: '',
  monthlyHOA: '',
  
  // Loan Structure
  amortizationType: 'Fully Amortizing',
  prepayPenalty: '36 Months',
  lockPeriod: '30 Days',
  extensionDays: '0 Days',
  
  // Rate Selection
  maxYieldMode: true,
  selectedRate: '',
  targetMinPrice: '100',
  
  // Broker Comp
  brokerCompPercent: '',
};

const ChurchillDSCRCalculator = ({ loanOfficer, onSave }) => {
  const [inputs, setInputs] = useState(initialInputs);
  const [showLLPABreakdown, setShowLLPABreakdown] = useState(false);
  const [customRateSheets, setCustomRateSheets] = useState({});
  const [isLoadingRateSheets, setIsLoadingRateSheets] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgram, setUploadProgram] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  
  // Check if current user is admin (can upload rate sheets)
  const isAdmin = checkIsAdmin(loanOfficer);
  
  // Update input handler
  const updateInput = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };
  
  // Get available product types for selected program
  const availableProductTypes = useMemo(() => {
    return getProductTypes(inputs.programName);
  }, [inputs.programName]);
  
  // Get available rates for selected program and product type
  const availableRates = useMemo(() => {
    const customSheet = customRateSheets[inputs.programName];
    return getRateMatrix(inputs.programName, inputs.productType, customSheet);
  }, [inputs.programName, inputs.productType, customRateSheets]);
  
  // Calculate metrics
  const metrics = useMemo(() => {
    return calculateChurchillMetrics(inputs, customRateSheets);
  }, [inputs, customRateSheets]);
  
  // Load rate sheets from backend
  const loadRateSheets = async () => {
    setIsLoadingRateSheets(true);
    try {
      const response = await fetch(`${API_URL}/api/rate-sheets`);
      if (response.ok) {
        const data = await response.json();
        const sheets = {};
        for (const sheet of data) {
          sheets[sheet.program_name] = sheet;
        }
        setCustomRateSheets(sheets);
      }
    } catch (error) {
      console.error('Error loading rate sheets:', error);
    } finally {
      setIsLoadingRateSheets(false);
    }
  };
  
  // Load rate sheets on mount
  useEffect(() => {
    loadRateSheets();
  }, []);
  
  // Handle rate sheet upload (admin only)
  const handleUploadRateSheet = async () => {
    if (!uploadFile || !uploadProgram || !isAdmin) return;
    
    setIsUploading(true);
    setUploadMessage('');
    
    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = btoa(e.target.result);
        
        const response = await fetch(`${API_URL}/api/rate-sheets`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            program_name: uploadProgram,
            csv_data: base64Data,
            officer_id: loanOfficer.id,
          }),
        });
        
        if (response.ok) {
          setUploadMessage('Rate sheet uploaded successfully!');
          setUploadFile(null);
          setUploadProgram('');
          loadRateSheets(); // Reload rate sheets
        } else {
          const error = await response.json();
          setUploadMessage(`Error: ${error.detail || 'Upload failed'}`);
        }
        setIsUploading(false);
      };
      reader.readAsBinaryString(uploadFile);
    } catch (error) {
      setUploadMessage(`Error: ${error.message}`);
      setIsUploading(false);
    }
  };
  
  // When program changes, reset product type if not available
  useEffect(() => {
    if (!availableProductTypes.includes(inputs.productType)) {
      updateInput('productType', availableProductTypes[0] || '30Y Fixed');
    }
  }, [inputs.programName, availableProductTypes, inputs.productType]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Left Column - Inputs */}
      <div className="lg:col-span-3 space-y-6">
        {/* Program Selection */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
              <Target className="w-4 h-4" strokeWidth={1.5} />
              Program Selection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-[#637888] uppercase tracking-wide">
                  Churchill Program
                </Label>
                <Select
                  value={inputs.programName}
                  onValueChange={(v) => updateInput('programName', v)}
                >
                  <SelectTrigger data-testid="select-churchill-program" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROGRAM_OPTIONS.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#637888] uppercase tracking-wide">
                  Product Type
                </Label>
                <Select
                  value={inputs.productType}
                  onValueChange={(v) => updateInput('productType', v)}
                >
                  <SelectTrigger data-testid="select-product-type" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableProductTypes.map(pt => (
                      <SelectItem key={pt} value={pt}>{pt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Property Information */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
              <Building2 className="w-4 h-4" strokeWidth={1.5} />
              Property Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="projectName" className="text-xs text-[#637888] uppercase tracking-wide">
                  Project Name
                </Label>
                <Input
                  id="projectName"
                  data-testid="input-project-name"
                  value={inputs.projectName}
                  onChange={(e) => updateInput('projectName', e.target.value)}
                  placeholder="Enter project name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="propertyAddress" className="text-xs text-[#637888] uppercase tracking-wide">
                  Property Address
                </Label>
                <Input
                  id="propertyAddress"
                  data-testid="input-property-address"
                  value={inputs.propertyAddress}
                  onChange={(e) => updateInput('propertyAddress', e.target.value)}
                  placeholder="Enter property address"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-[#637888] uppercase tracking-wide">
                  State
                </Label>
                <Select
                  value={inputs.propertyState}
                  onValueChange={(v) => updateInput('propertyState', v)}
                >
                  <SelectTrigger data-testid="select-property-state" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#637888] uppercase tracking-wide">
                  Property Type
                </Label>
                <Select
                  value={inputs.propertyType}
                  onValueChange={(v) => updateInput('propertyType', v)}
                >
                  <SelectTrigger data-testid="select-property-type" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPE_OPTIONS.map(pt => (
                      <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loan Details */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
              <DollarSign className="w-4 h-4" strokeWidth={1.5} />
              Loan Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="propertyValue" className="text-xs text-[#637888] uppercase tracking-wide">
                  Property Value
                </Label>
                <CurrencyInput
                  id="propertyValue"
                  testId="input-property-value"
                  value={inputs.propertyValue}
                  onChange={(v) => updateInput('propertyValue', v)}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="loanAmount" className="text-xs text-[#637888] uppercase tracking-wide">
                  Loan Amount
                </Label>
                <CurrencyInput
                  id="loanAmount"
                  testId="input-loan-amount"
                  value={inputs.loanAmount}
                  onChange={(v) => updateInput('loanAmount', v)}
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-xs text-[#637888] uppercase tracking-wide">
                  LTV
                </Label>
                <div className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded-md h-[38px] flex items-center">
                  <span className="font-mono-number text-slate-700">
                    {formatPercentage(metrics?.ltv || 0)}
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="text-xs text-[#637888] uppercase tracking-wide">
                  Loan Purpose
                </Label>
                <Select
                  value={inputs.loanPurpose}
                  onValueChange={(v) => updateInput('loanPurpose', v)}
                >
                  <SelectTrigger data-testid="select-loan-purpose" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOAN_PURPOSE_OPTIONS.map(lp => (
                      <SelectItem key={lp.value} value={lp.value}>{lp.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="ficoScore" className="text-xs text-[#637888] uppercase tracking-wide">
                  FICO Score
                </Label>
                <Input
                  id="ficoScore"
                  data-testid="input-fico"
                  type="number"
                  value={inputs.ficoScore}
                  onChange={(e) => updateInput('ficoScore', e.target.value)}
                  placeholder="740"
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* DSCR Information */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
              <TrendingUp className="w-4 h-4" strokeWidth={1.5} />
              DSCR Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="dscrRatio" className="text-xs text-[#637888] uppercase tracking-wide">
                  DSCR Ratio
                </Label>
                <Input
                  id="dscrRatio"
                  data-testid="input-dscr"
                  type="number"
                  step="0.01"
                  value={inputs.dscrRatio}
                  onChange={(e) => updateInput('dscrRatio', e.target.value)}
                  placeholder="1.25"
                  className="mt-1 font-mono-number"
                />
              </div>
              <div>
                <Label htmlFor="monthlyRent" className="text-xs text-[#637888] uppercase tracking-wide">
                  Monthly Rent
                </Label>
                <CurrencyInput
                  id="monthlyRent"
                  testId="input-monthly-rent"
                  value={inputs.monthlyRent}
                  onChange={(v) => updateInput('monthlyRent', v)}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="monthlyTaxes" className="text-xs text-[#637888] uppercase tracking-wide">
                  Monthly Taxes
                </Label>
                <CurrencyInput
                  id="monthlyTaxes"
                  testId="input-monthly-taxes"
                  value={inputs.monthlyTaxes}
                  onChange={(v) => updateInput('monthlyTaxes', v)}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="monthlyInsurance" className="text-xs text-[#637888] uppercase tracking-wide">
                  Monthly Insurance
                </Label>
                <CurrencyInput
                  id="monthlyInsurance"
                  testId="input-monthly-insurance"
                  value={inputs.monthlyInsurance}
                  onChange={(v) => updateInput('monthlyInsurance', v)}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <Label htmlFor="monthlyHOA" className="text-xs text-[#637888] uppercase tracking-wide">
                  Monthly HOA
                </Label>
                <CurrencyInput
                  id="monthlyHOA"
                  testId="input-monthly-hoa"
                  value={inputs.monthlyHOA}
                  onChange={(v) => updateInput('monthlyHOA', v)}
                  placeholder="0"
                />
              </div>
              <div>
                <Label className="text-xs text-[#637888] uppercase tracking-wide">
                  Monthly PITIA (Calculated)
                </Label>
                <div className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded-md h-[38px] flex items-center">
                  <span className="font-mono-number text-slate-700">
                    {formatCurrency(metrics?.monthlyPITIA || 0)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loan Structure */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
              <Landmark className="w-4 h-4" strokeWidth={1.5} />
              Loan Structure
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs text-[#637888] uppercase tracking-wide">
                  Amortization
                </Label>
                <Select
                  value={inputs.amortizationType}
                  onValueChange={(v) => updateInput('amortizationType', v)}
                >
                  <SelectTrigger data-testid="select-amortization" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fully Amortizing">Fully Amortizing</SelectItem>
                    <SelectItem value="Interest Only">Interest Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#637888] uppercase tracking-wide">
                  Prepayment Penalty
                </Label>
                <Select
                  value={inputs.prepayPenalty}
                  onValueChange={(v) => updateInput('prepayPenalty', v)}
                >
                  <SelectTrigger data-testid="select-prepay" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PREPAY_PENALTY_OPTIONS.map(pp => (
                      <SelectItem key={pp.value} value={pp.value}>{pp.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#637888] uppercase tracking-wide">
                  Lock Period
                </Label>
                <Select
                  value={inputs.lockPeriod}
                  onValueChange={(v) => updateInput('lockPeriod', v)}
                >
                  <SelectTrigger data-testid="select-lock-period" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCK_PERIOD_OPTIONS.map(lp => (
                      <SelectItem key={lp.value} value={lp.value}>{lp.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rate Selection */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-[#002F87] font-heading text-sm uppercase tracking-wide">
              <Receipt className="w-4 h-4" strokeWidth={1.5} />
              Rate & Pricing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center gap-3 pt-5">
                <input
                  type="checkbox"
                  id="maxYieldMode"
                  data-testid="checkbox-max-yield"
                  checked={inputs.maxYieldMode}
                  onChange={(e) => updateInput('maxYieldMode', e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300"
                />
                <Label htmlFor="maxYieldMode" className="text-sm text-slate-700 cursor-pointer">
                  Max Yield Mode (Auto-select best rate)
                </Label>
              </div>
              
              {!inputs.maxYieldMode && (
                <div>
                  <Label className="text-xs text-[#637888] uppercase tracking-wide">
                    Select Rate
                  </Label>
                  <Select
                    value={inputs.selectedRate}
                    onValueChange={(v) => updateInput('selectedRate', v)}
                  >
                    <SelectTrigger data-testid="select-rate" className="mt-1">
                      <SelectValue placeholder="Select rate..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableRates.map(r => (
                        <SelectItem key={r.rate} value={String(r.rate)}>
                          {r.rate.toFixed(3)}% (Base: {r.price.toFixed(3)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label htmlFor="brokerComp" className="text-xs text-[#637888] uppercase tracking-wide">
                  Broker Comp %
                </Label>
                <PercentageInput
                  id="brokerComp"
                  testId="input-broker-comp"
                  value={inputs.brokerCompPercent}
                  onChange={(v) => updateInput('brokerCompPercent', v)}
                  placeholder="0"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Admin Rate Sheet Upload - Only visible to George */}
        {isAdmin && (
          <Card className="border border-amber-200 shadow-sm bg-amber-50">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-amber-700 font-heading text-sm uppercase tracking-wide">
                <Upload className="w-4 h-4" strokeWidth={1.5} />
                Admin: Upload Rate Sheet (CSV)
              </CardTitle>
              <p className="text-xs text-amber-600 mt-1">
                Only you can upload/update rate sheets. Changes affect all users immediately.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-amber-700 uppercase tracking-wide">
                    Program
                  </Label>
                  <Select
                    value={uploadProgram}
                    onValueChange={setUploadProgram}
                  >
                    <SelectTrigger className="mt-1 border-amber-300">
                      <SelectValue placeholder="Select program..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PROGRAM_OPTIONS.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-amber-700 uppercase tracking-wide">
                    CSV File
                  </Label>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="mt-1 border-amber-300"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleUploadRateSheet}
                    disabled={!uploadFile || !uploadProgram || isUploading}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    {isUploading ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload
                      </>
                    )}
                  </Button>
                </div>
              </div>
              {uploadMessage && (
                <p className={`mt-2 text-sm ${uploadMessage.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
                  {uploadMessage}
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Right Column - Summary */}
      <div className="lg:col-span-2">
        <div className="sticky top-6 space-y-4">
          {/* Summary Card */}
          <Card className="border border-slate-200 shadow-lg overflow-hidden">
            <div className="h-1 bg-[#FFFF00]" />
            <CardHeader className="pb-4 bg-[#002F87] text-white">
              <CardTitle className="flex items-center gap-2 font-heading text-sm uppercase tracking-wide">
                <FileStack className="w-4 h-4" strokeWidth={1.5} />
                Churchill DSCR Summary
              </CardTitle>
              <p className="text-xs text-white/70 mt-1">
                {inputs.programName} • {inputs.productType}
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 p-4 bg-[#002F87] rounded-md">
                    <p className="text-xs text-white/80 uppercase tracking-wide mb-1">Selected Rate</p>
                    <p className="font-mono-number text-2xl font-bold text-white">
                      {metrics?.selectedRate ? `${metrics.selectedRate.toFixed(3)}%` : '-'}
                    </p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-md border border-green-200">
                    <p className="text-xs text-green-700 uppercase tracking-wide mb-1">Adjusted Price</p>
                    <p className="font-mono-number text-lg font-semibold text-green-700">
                      {metrics?.adjustedPrice?.toFixed(3) || '-'}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
                    <p className="text-xs text-blue-700 uppercase tracking-wide mb-1">YSP Amount</p>
                    <p className="font-mono-number text-lg font-semibold text-blue-700">
                      {formatCurrency(metrics?.yspAmount || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                    <p className="text-xs text-slate-600 uppercase tracking-wide mb-1">Monthly Payment</p>
                    <p className="font-mono-number text-lg font-semibold text-slate-900">
                      {formatCurrency(metrics?.monthlyPayment || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                    <p className="text-xs text-slate-600 uppercase tracking-wide mb-1">LTV</p>
                    <p className="font-mono-number text-lg font-semibold text-slate-900">
                      {formatPercentage(metrics?.ltv || 0)}
                    </p>
                  </div>
                </div>

                <Separator />

                {/* LLPA Breakdown Toggle */}
                <div>
                  <button
                    onClick={() => setShowLLPABreakdown(!showLLPABreakdown)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide">
                      LLPA Breakdown ({metrics?.llpaAdjustments?.length || 0} Adjustments)
                    </h3>
                    {showLLPABreakdown ? (
                      <ChevronUp className="w-4 h-4 text-[#002F87]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#002F87]" />
                    )}
                  </button>
                  
                  {showLLPABreakdown && (
                    <div className="mt-3 space-y-2 text-sm">
                      <div className="flex justify-between text-slate-600 pb-2 border-b border-slate-200">
                        <span>Base Price</span>
                        <span className="font-mono-number">{metrics?.basePrice?.toFixed(3) || '-'}</span>
                      </div>
                      {metrics?.llpaAdjustments?.map((adj, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span className="text-slate-600">{adj.category}: {adj.description}</span>
                          <span className={`font-mono-number ${adj.adjustment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {adj.adjustment >= 0 ? '+' : ''}{adj.adjustment.toFixed(3)}
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between font-semibold pt-2 border-t border-slate-200">
                        <span className="text-[#002F87]">Total LLPA</span>
                        <span className={`font-mono-number ${(metrics?.totalLLPAAdjustment || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {(metrics?.totalLLPAAdjustment || 0) >= 0 ? '+' : ''}{(metrics?.totalLLPAAdjustment || 0).toFixed(3)}
                        </span>
                      </div>
                      <div className="flex justify-between font-bold pt-2 border-t-2 border-slate-300">
                        <span className="text-[#002F87]">Final Price</span>
                        <span className="font-mono-number text-[#002F87]">{metrics?.adjustedPrice?.toFixed(3) || '-'}</span>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Loan Details */}
                <div>
                  <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide mb-3">
                    Loan Details
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Loan Amount</span>
                      <span className="font-mono-number text-slate-900">
                        {formatCurrency(parseFloat(inputs.loanAmount) || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Property Type</span>
                      <span className="text-slate-900">{inputs.propertyType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Loan Purpose</span>
                      <span className="text-slate-900">{inputs.loanPurpose}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">FICO</span>
                      <span className="font-mono-number text-slate-900">{inputs.ficoScore || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">DSCR Ratio</span>
                      <span className="font-mono-number text-slate-900">{inputs.dscrRatio || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Prepay Penalty</span>
                      <span className="text-slate-900">{inputs.prepayPenalty}</span>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Payment Summary */}
                <div>
                  <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide mb-3">
                    Payment Summary
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Monthly P&I</span>
                      <span className="font-mono-number text-slate-900">
                        {formatCurrency(metrics?.monthlyPI || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Monthly IO</span>
                      <span className="font-mono-number text-slate-900">
                        {formatCurrency(metrics?.monthlyIO || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Monthly PITIA</span>
                      <span className="font-mono-number text-slate-900">
                        {formatCurrency(metrics?.monthlyPITIA || 0)}
                      </span>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Compensation */}
                <div>
                  <h3 className="font-heading font-semibold text-sm text-[#002F87] uppercase tracking-wide mb-3">
                    Compensation
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">YSP %</span>
                      <span className="font-mono-number text-slate-900">
                        {(metrics?.yspPercent || 0).toFixed(3)}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">YSP Amount</span>
                      <span className="font-mono-number text-slate-900">
                        {formatCurrency(metrics?.yspAmount || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Broker Comp</span>
                      <span className="font-mono-number text-slate-900">
                        {formatCurrency(metrics?.brokerComp || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-[#002F87]">
                      <span>Net YSP</span>
                      <span className="font-mono-number">
                        {formatCurrency(metrics?.netYSP || 0)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* PDF Download */}
                <div className="pt-4">
                  <PDFDownloadLink
                    document={
                      <ChurchillDSCRPDF 
                        data={metrics} 
                        inputs={inputs} 
                        loanOfficer={loanOfficer} 
                      />
                    }
                    fileName={`Churchill_DSCR_${inputs.propertyAddress || 'Quote'}_${new Date().toISOString().split('T')[0]}.pdf`}
                    className="w-full"
                  >
                    {({ loading }) => (
                      <Button 
                        className="w-full bg-[#002F87] hover:bg-[#002F87]/90 text-white"
                        disabled={loading}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {loading ? 'Generating PDF...' : 'Download PDF Summary'}
                      </Button>
                    )}
                  </PDFDownloadLink>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChurchillDSCRCalculator;
